// Every Firestore path, field name and event constant the admin reads or writes. No literals elsewhere.
// Contract: services/whatsapp/wa_core/{ledger,publish}.py, sinks/run_log.py, functions/wa/store.py.

export const EVENT = {
  /** events/<key>/… in Firestore */
  key: "MumbaiNavaratri2026",
  /** services/whatsapp/events/<slug>.yaml, used in the CLI command */
  slug: "mumbai_navaratri_2026",
  name: "Mumbai Navaratri 2026",
  org: "Vaidic Dharma Sansthan, Mumbai",
  dates: "16–18 Oct 2026",
} as const;

/** Same allowlist as the old admin and functions/wa/logic.check_admin. */
export const ADMIN_EMAILS: readonly string[] = ["vedantaparikh@gmail.com"];

export const FUNCTIONS_REGION = "asia-south1";
export const FN_WA_REPLY = "wa_reply";
/** Releases over-limit (131048) ledger rows for the next send cycle (functions/main.py:wa_retry). */
export const FN_WA_RETRY = "wa_retry";

/** Emulator ports; keep in step with the repo-root firebase.emulators.json. */
export const EMULATOR = { host: "127.0.0.1", auth: 9099, firestore: 8085, functions: 5001 } as const;
export const EMULATOR_PROJECT_ID = "demo-ticketvalidator";

export const PATH = {
  ledger: (e: string) => `events/${e}/wa_messages`,
  recipients: (e: string) => `events/${e}/wa_recipients`,
  runs: (e: string) => `events/${e}/wa_runs`,
  config: (e: string) => `events/${e}/wa_meta/config`,
  /** sender heartbeat, one write per live cycle (sinks/run_log.FirestoreHeartbeat) */
  sender: (e: string) => `events/${e}/wa_meta/sender`,
  conversations: "wa_conversations",
  conversation: (phone: string) => `wa_conversations/${phone}`,
  thread: (phone: string) => `wa_conversations/${phone}/messages`,
  optouts: "wa_optouts",
} as const;

/** events/<E>/wa_messages/{key} — one row per registration (ledger.py). */
export const LEDGER = {
  status: "status",
  phone: "phone",
  name: "name",
  wamid: "wamid",
  template: "template",
  vars: "vars",
  sentAt: "sent_at",
  deliveredAt: "delivered_at",
  readAt: "read_at",
  failedAt: "failed_at",
  lastAttemptAt: "last_attempt_at",
  /** cap window start: send time, then Meta's delivery time (ledger.window_time) */
  windowAt: "window_at",
  /** set by wa_retry; cleared by the sender's next claim */
  retryApprovedAt: "retry_approved_at",
  attempts: "attempts",
  error: "error",
} as const;

/** events/<E>/wa_recipients/{key} — written by `wa publish` (publish.py). Doc id = ledger key. */
export const RECIPIENT = {
  key: "key",
  baseKey: "base_key",
  phone: "phone",
  rawPhone: "raw_phone",
  phoneOk: "phone_ok",
  phoneError: "phone_error",
  name: "name",
  days: "days",
  /** rendered text ("Fri 16 Oct 8:30 AM & 6:00 PM · …"), not the raw sheet value */
  dates: "dates",
  /** ["16 Morning", "17 Evening"]: parsed from the raw sheet column (EventConfig.session_labels) */
  sessions: "sessions",
  sheetTs: "sheet_ts",
  /** the exact template params `wa send` would send; absent when the render failed */
  params: "params",
  publishedAt: "published_at",
} as const;

/** events/<E>/wa_meta/config — written by `wa publish` (cli.config_doc). */
export const CONFIG = {
  eventName: "event_name",
  template: "template",
  templateBody: "template_body",
  params: "params",
  dailyCap: "daily_cap",
  source: "source",
  publishedAt: "published_at",
} as const;

/** events/<E>/wa_runs/{runId} — written by `wa send --live` (sinks/run_log.py). */
export const RUN = {
  startedAt: "started_at",
  finishedAt: "finished_at",
  by: "by",
  limit: "limit",
  template: "template",
  attemptedPhones: "attempted_phones",
  sent: "sent",
  failed: "failed",
  invalid: "invalid",
  stopReason: "stop_reason",
} as const;

/** events/<E>/wa_meta/sender — heartbeat from `wa send --live`. */
export const SENDER = {
  lastCycleAt: "last_cycle_at",
} as const;

/** wa_conversations/{phone} (store.py, ledger.mark_sent). Phone id = digits, no "+". */
export const CONV = {
  name: "name",
  profileName: "profile_name",
  lastAt: "last_at",
  lastInboundAt: "last_inbound_at",
  lastPreview: "last_preview",
  unread: "unread",
  optedOut: "opted_out",
  regIds: "reg_ids",
  events: "events",
} as const;

/** wa_conversations/{phone}/messages/{id} */
export const MSG = {
  dir: "dir",
  kind: "kind",
  text: "text",
  template: "template",
  regId: "reg_id",
  ledgerPath: "ledger_path",
  status: "status",
  at: "at",
  sentBy: "sent_by",
  error: "error",
  /** {emoji, at, by, wamid} on their message when we reacted (store.send_reaction); null once cleared */
  ourReaction: "our_reaction",
} as const;

/**
 * Event sessions in date order. `key` = the snapshot's `sessions` values (= session_times keys in
 * services/whatsapp/events/<slug>.yaml); `time` = Puja start. The config doc has no session_times.
 */
export const SESSIONS: readonly { key: string; label: string; time: string }[] = [
  { key: "16 Morning", label: "Fri 16 · Morning", time: "8:30 AM" },
  { key: "16 Evening", label: "Fri 16 · Evening", time: "6:00 PM" },
  { key: "17 Morning", label: "Sat 17 · Morning", time: "8:00 AM" },
  { key: "17 Evening", label: "Sat 17 · Evening", time: "6:00 PM" },
  { key: "18 Morning", label: "Sun 18 · Morning", time: "7:30 AM" },
];

export const THREAD_LIMIT = 500;
/** Meta's customer-service window and the limiter's rolling window. */
export const DAY_MS = 24 * 3600 * 1000;
